import { type ZoomFpsConfig } from "../types/MapConfiguration";

/**
 * Application-wide diagnostics toggles (memory profiling, etc.).
 */
const memoryProfilingEnvEnabled =
    import.meta.env.VITE_MEMORY_PROFILING === "true" ||
      import.meta.env.VITE_MEMORY_PROFILING === "1";

const memoryProfilingActive = import.meta.env.DEV || memoryProfilingEnvEnabled;

export const APP_DIAGNOSTICS = {
    /**
     * Log estimated in-heap size for major subsystems (realtime, stores, map layers).
     * Enabled by default in Vite dev; enable in production via VITE_MEMORY_PROFILING=true.
     */
    "memoryProfilingEnabled": memoryProfilingActive,

    /** Minimum interval between throttled memory logs (ms). Contexts with `skipThrottle` bypass this. */
    "memoryProfilingMinIntervalMs": 10_000,

    /** Interval for optional periodic memory snapshots (ms). Set to 0 to disable. */
    "memoryProfilingPeriodicIntervalMs": memoryProfilingActive ? 120_000 : 0,

    /** Minimum byte delta shown in category tables and TOP Δ lines. */
    "memoryDeltaMinBytes": 512 * 1024,

    /** Reuse slice / MapLibre stringify metrics from the last full scan when younger than this (ms). */
    "memoryFullScanMaxAgeMs": 120_000,

    /** Debounce before reacting to map idle for memory logging (ms). */
    "memoryIdleDebounceMs": 2_000,

    /** Minimum interval between map-idle memory logs (ms). */
    "memoryIdleThrottleMs": 30_000,

    /** Heap delta (bytes) that upgrades map-idle from standard to full scan. */
    "memoryIdleFullScanHeapDeltaBytes": 2 * 1024 * 1024,

    /** Schedule periodic / idle full scans via requestIdleCallback when available. */
    "memoryDeferFullScan": true,
};

/**
 * Search bar configuration: refresh button visibility and time check interval.
 */
export const SEARCH_BAR = {
    /**
     * Duration of the arrival field `top` CSS transition (Tailwind
     * `transition-top`). Home samples layout on each animation frame during
     * this window so the floating menu tracks the moving search chrome.
     */
    "topLayoutTransitionMs": 300,

    /**
     * Minimum minutes difference to show calendar refresh button.
     *
     * When the current time (rounded to minute) exceeds the selected arrival time
     * (rounded to minute) by at least this number of minutes, a refresh button
     * is displayed in the calendar section to allow updating to the current time.
     */
    "displayRefreshButtonMinMinutesDifference": 1,

    /** Interval to check if the refresh button should be displayed (ms). */
    "refreshTimeIntervalMs": 60_000,
};

/**
 * FPS limitation configuration for all animations and rendering.
 * Use 0 to disable throttling for individual settings.
 */
export const FPS_LIMITATION = {
    /**
     * Maximum MapLibre render rate (fps).
     */
    "mapLibreRender": 30,

    /**
     * Maximum navigation animation FPS (cursor + camera updates).
     */
    "navigationAnimation": 30,

    /**
     * Vehicle position update FPS configuration based on zoom level.
     * Lower zoom levels (overview) use lower FPS to reduce CPU/GPU load.
     * Higher zoom levels (detail) use higher FPS for smoother animations.
     *
     * Set to null to disable throttling entirely.
     *
     * [zoom >= x, y FPS]
     */
    "vehiclePositionUpdate": [
        [ 18, 60 ],
        [ 16, 30 ],
        [ 15, 20 ],
        [ 14, 10 ],
        [ 0, 5 ],
    ] as ZoomFpsConfig,
};

/**
 * MapLibre transit stop layers: minimum zoom for each display mode.
 */
export const MAP_STOPS_LAYER_ZOOM = {
    /**
     * Minimum zoom at which parent station markers appear (overview stops).
     * Below this, all stop layers stay hidden.
     */
    "parentStationsMinZoom": 13.5,

    /**
     * Minimum zoom at which per-platform markers and labels replace parent-only view.
     * Between parentStationsMinZoom and this value, only parents are shown.
     */
    "stopPlatformsMinZoom": 16.4,
};

/**
 * Stop map preload: bbox cache, proactive fetch near edges, and viewport debounce.
 */
export const MAP_STOPS_PRELOAD = {
    /**
     * Half-axis multiplier applied after reference bbox at parent zoom (linear ×4 → ~×16 area).
     */
    "linearExtentMultiplier": 4,

    /**
     * Zoom used to size the reference viewport for prefetch bbox (defaults to parent overview).
     */
    "referenceZoom": MAP_STOPS_LAYER_ZOOM.parentStationsMinZoom,

    /**
     * Subtracted from parentStationsMinZoom for minimum zoom at which preload/check/fetch run
     * (default 1 → 13.5 - 1 = 12.5).
     */
    "minPreloadZoomBelowParent": 1,

    /**
     * When the visible viewport exits this fraction of preloadedBbox (shrink from center),
     * trigger prefetch before the outer edge (default 0.85).
     */
    "prefetchTriggerShrinkFactor": 0.85,

    /** Debounce before updateStopPreloadIfNeeded after viewport changes (ms). */
    "viewportDebounceMs": 300,
};

/**
 * Minimum map zoom at which stop preload and fetch are active.
 */
export const MAP_STOPS_MIN_PRELOAD_ZOOM =
    MAP_STOPS_LAYER_ZOOM.parentStationsMinZoom - MAP_STOPS_PRELOAD.minPreloadZoomBelowParent;

/**
 * Bike station layers: minimum zoom at which stations are visible on the map.
 */
export const MAP_BIKE_STATIONS_LAYER_ZOOM = {
    /** Minimum zoom at which bike station markers appear. */
    "minZoom": 15.0,
};

/**
 * Bike station map preload: bbox cache, proactive fetch near edges, and viewport debounce.
 */
export const MAP_BIKE_STATIONS_PRELOAD = {
    /** Half-axis multiplier applied after reference bbox at min display zoom. */
    "linearExtentMultiplier": 4,

    /** Zoom used to size the reference viewport for prefetch bbox. */
    "referenceZoom": MAP_BIKE_STATIONS_LAYER_ZOOM.minZoom,

    /** Subtracted from minZoom for minimum zoom at which preload/check/fetch run (16.5 - 1 = 15.5). */
    "minPreloadZoomBelowReference": 1,

    /** When the visible viewport exits this fraction of preloadedBbox (shrink from center), trigger prefetch. */
    "prefetchTriggerShrinkFactor": 0.85,

    /** Debounce before updating preload after viewport changes (ms). */
    "viewportDebounceMs": 300,

    /** Whether to force a refresh when the page/ tab becomes visible again. */
    "refreshOnPageVisible": true,
};

/**
 * Minimum map zoom at which bike station preload and fetch are active.
 */
export const MAP_BIKE_STATIONS_MIN_PRELOAD_ZOOM =
    MAP_BIKE_STATIONS_LAYER_ZOOM.minZoom - MAP_BIKE_STATIONS_PRELOAD.minPreloadZoomBelowReference;

/**
 * MapLibre constructor options that bound in-memory tile retention (per vector/raster source).
 *
 * @remarks MapLibre defaults grow the cache with viewport and zoom history; these caps trade
 * a small risk of brief tile reloads after fast pan/zoom for lower JS/GPU memory (see
 * `[AppMemory]` unaccounted after `map-idle`). PWA tile cache (`vite.config.ts` Workbox) is separate (disk).
 */
export const MAP_LIBRE = {
    /**
     * How many zoom levels of tiles to retain around the viewport (MapLibre default is 5).
     * Tile count per source stays dynamically sized from the viewport × this value.
     */
    "maxTileCacheZoomLevels": 3,

    /**
     * Drop in-flight tile requests for non-current zoom levels while zooming in (less peak memory).
     */
    "cancelPendingTileRequestsWhileZooming": true,
};

/**
 * Realtime transport configuration: polling, caching, and interpolation.
 */
export const REALTIME = {
    /** Base URL path for the Rust realtime transport API. */
    "apiBase": "/realtime-api",

    /** Interval between periodic snapshot polls (ms). */
    "pollIntervalMs": 60_000,

    /** Debounce delay before reacting to viewport changes (ms). */
    "viewportDebounceMs": 500,

    /** Factor by which the viewport bbox is expanded to prefetch surrounding data. */
    "viewportExpandFactor": 1.5,

    /** Interval between stale-cache eviction sweeps (ms). */
    "evictIntervalMs": 60_000,

    /** Time-to-live for unused shapes before eviction (ms). */
    "shapeTtlMs": 10 * 60 * 1000,

    /** Time-to-live for unused schedules/timelines before eviction (ms). */
    "scheduleTtlMs": 5 * 60 * 1000,

    /**
     * Time margin (in seconds) applied around the current time when filtering
     * visible trips from the trip index. Trips whose time range falls within
     * [now - margin, now + margin] are considered visible.
     */
    "visibleTripMarginS": 30 * 60,

    /**
     * Distance threshold (meters) at which bearing interpolation begins
     * when a vehicle approaches a turning point on the timeline.
     */
    "turningThresholdM": 5,

    /** Enable/disable smooth catch-up when realtime delay changes abruptly. */
    "delaySmoothingEnabled": true,

    /** Minimum smoothing duration applied to delay transitions (ms). */
    "delaySmoothingMinMs": 1_500,

    /** Maximum smoothing duration applied to delay transitions (ms). */
    "delaySmoothingMaxMs": 30_000,

    /**
     * Adaptive smoothing factor in milliseconds per second of delay jump.
     * Example: a 10s delay jump -> 12s transition when set to 1200.
     */
    "delaySmoothingMsPerDelaySecond": 1_200,

    /**
     * Ignore micro-jitters below this delay delta (seconds).
     * Small changes are applied immediately to avoid unnecessary lag.
     */
    "delaySmoothingJumpThresholdS": 2,
};

/**
 * Map stop label configuration for itineraries and vehicle-click overlays.
 *
 * Each context (itinerary leg selection vs. vehicle click) has its own pair
 * of toggles so the user can independently enable/disable arrival times
 * and delay/advance indicators.
 */
/**
 * Screen-space collision settings for HTML stop label markers on itineraries
 * and vehicle-click route overlays.
 */
export const STOP_LABEL_COLLISION = {
    /** Extra margin around each label bounding box when testing overlap (px). */
    "textPadding": 4,

    /** Minimum interval between collision passes during continuous pan/zoom (ms). */
    "viewportRefreshThrottleMs": 500,

    /** Stacking order for stop dot HTML markers (labels use {@link labelMarkerZIndex}). */
    "dotMarkerZIndex": 1,

    /** Stacking order for stop label HTML markers — always above dots. */
    "labelMarkerZIndex": 2,

    /** Fade duration when stop labels show or hide via collision (ms). */
    "labelFadeDurationMs": 600,
};

export const MAP_STOP_LABELS = {
    /** Whether clicking a bus on the map shows its realtime transit path overlay. */
    "showRealtimeTransitPathOnVehicleClick": true,

    /** Interval (ms) for refreshing the current-segment polyline (bus → next stop). */
    "currentSegmentRefreshMs": 500,

    /**
     * How often to re-evaluate schedule time vs next stop (remaining polyline + markers).
     * Redraw runs only when the next-stop index or delay/visual state actually changes.
     */
    "scheduleWatchIntervalMs": 1_000,

    /** Itinerary leg stop labels. */
    "itinerary": {
        /** Show scheduled arrival time next to stop names. */
        "showArrivalTime": true,

        /** Show delay/advance badge below stop names. */
        "showDelayStatus": true,

        /** Show transfer line badges on a second label row. */
        "showCorrespondences": true,
    },

    /** Realtime transit path stop labels (vehicle click). */
    "realtimeTransitPath": {
        /** Show scheduled arrival time next to stop names. */
        "showArrivalTime": true,

        /** Show delay/advance badge below stop names. */
        "showDelayStatus": true,

        /** Show transfer line badges on a second label row. */
        "showCorrespondences": true,
    },
};

/**
 * Vehicle lifecycle management: cleanup thresholds and intervals.
 */
export const VEHICLE = {
    /** Grace period after a vehicle's last timeline timestamp before removal (ms). */
    "positionGraceMs": 60_000,

    /** A vehicle not updated for this duration is considered stale and removed (ms). */
    "staleUpdateMs": 5 * 60 * 1000,

    /** Duration considered "recent" for a vehicle update (ms). */
    "recentUpdateMs": 2 * 60 * 1000,

    /** Interval for the periodic orphan-vehicle cleanup sweep (ms). */
    "cleanupIntervalMs": 60_000,
};
